import { Injectable, OnDestroy } from '@angular/core';
import { environment } from './../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject, firstValueFrom, of } from 'rxjs';
import { catchError, defaultIfEmpty, map, takeUntil } from 'rxjs/operators';

export enum AddressVerificationStatus {
  VERIFIED = 'verified',
  UNVERIFIED = 'unverified',
  SUGGESTED = 'suggested',
  UNDELIVERABLE = 'undeliverable',
  SERVICEUNAVAILABLE = 'serviceUnavailable',
  UNKNOWN = 'unknown',
}

interface VerifyAddressResponse {
  Version: string;
  TransmissionReference: string;
  TransmissionResults: string;
  TotalRecords: string;
  Records: VerifyAddress[];
}

interface VerifyAddress {
  RecordID: string;
  Results: string;
  FormattedAddress: string;
  Organization: string;
  AddressLine1: string;
  AddressLine2: string;
  AddressLine3: string;
  AddressLine4: string;
  AddressLine5: string;
  AddressLine6: string;
  AddressLine7: string;
  AddressLine8: string;
  SubPremises: string;
  DoubleDependentLocality: string;
  DependentLocality: string;
  Locality: string;
  SubAdministrativeArea: string;
  AdministrativeArea: string;
  PostalCode: string;
  PostalCodeType: string;
  AddressType: string;
  AddressKey: string;
  SubNationalArea: string;
  CountryName: string;
  CountryISO3166_1_Alpha2: string;
  CountryISO3166_1_Alpha3: string;
  CountryISO3166_1_Numeric: string;
  CountrySubdivisionCode: string;
  Thoroughfare: string;
  ThoroughfarePreDirection: string;
  ThoroughfareLeadingType: string;
  ThoroughfareName: string;
  ThoroughfareTrailingType: string;
  ThoroughfarePostDirection: string;
  DependentThoroughfare: string;
  DependentThoroughfarePreDirection: string;
  DependentThoroughfareLeadingType: string;
  DependentThoroughfareName: string;
  DependentThoroughfareTrailingType: string;
  DependentThoroughfarePostDirection: string;
  Building: string;
  PremisesType: string;
  PremisesNumber: string;
  SubPremisesType: string;
  SubPremisesNumber: string;
  PostBox: string;
  Latitude: string;
  Longitude: string;
  DeliveryIndicator: string;
  MelissaAddressKey: string;
  MelissaAddressKeyBase: string;
  PostOfficeLocation: string;
  SubPremiseLevel: string;
  SubPremiseLevelType: string;
  SubPremiseLevelNumber: string;
  SubBuilding: string;
  SubBuildingType: string;
  SubBuildingNumber: string;
  UTC: string;
  DST: string;
  DeliveryPointSuffix: string;
  CensusKey: string;
  Extras: Record<string, string>;
}

export interface AddressOptions {
  suggestedAddress: Record<string, string> | null;
  addressUndeliverableMessage: string | null;
  lastUserVerifiedAddress: Record<string, string> | null;
  allowPOBox: boolean;
}

// Canonical warning/error copy surfaced during address verification.
export const WARNING_MESSAGES = {
  NOT_ALLOW_POBOX:
    'This address is identified as a PO Box. This address type does not allow the provision of a PO Box. Edit address fields below if you want to provide a new address',
  NOT_VERIFIED_ADDRESS:
    'This address is unable to be verified and is not deliverable. Edit address fields below if you want to provide a new address.',
  REQUIRED_ADDRESS_LINE2: 'Address Line 2 is required to verify address.',
  SERVER_ERROR:
    'Address verification is temporarily unavailable. Please review your address for accuracy before continuing.',
};

// Pre-save verification outcome: proceed (save), suggested (prompt), undeliverable (warn), error (service failed).
export type VerifySaveResult =
  | { action: 'proceed' }
  | { action: 'suggested' }
  | { action: 'undeliverable' }
  | { action: 'error' };
/**
 * Maps Canadian province codes to NMLS country dropdown values.
 * The NMLS dropdown encodes Canadian provinces as country-level entries
 * (e.g., "ONC" for Canada - Ontario) rather than using a separate state field.
 * Referenced from db table: `select * from COUNTRIES`
 */
const CANADA_PROVINCE_TO_NMLS: Record<string, string> = {
  AB: 'ABC',
  BC: 'BCC',
  MB: 'MBC',
  NB: 'NBC',
  NL: 'NLC',
  NT: 'NTC',
  NS: 'NSC',
  ON: 'ONC',
  PE: 'PEC',
  QC: 'QCC',
  SK: 'SKC',
  YT: 'YTC',
};

/**
 * Maps Melissa Address Change (AC) codes to whether they represent
 * a verified/cosmetic correction (true) or a substantive address change (false).
 * Used in rejectSuggestedAddress to determine behavior:
 * - All codes are Y (true): patch suggested values silently (cosmetic corrections only)
 * - Any code is N (false): set undeliverable (substantive address change rejected)
 */
const AC_CODE_VERIFIED_MAP: Record<string, boolean> = {
  AC01: false, // Postal code changed/added
  AC02: false, // State/province changed
  AC03: false, // City changed
  AC04: false, // Alternate to base (preferred) version changed (US only)
  AC05: true, // Alias name changed (abbreviation expanded, US only)
  AC06: false, // Address Line 1/2 swapped
  AC07: false, // Address1 & Company swapped
  AC08: true, // Plus4 changed (US only)
  AC09: false, // Dependent locality (urbanization) changed
  AC10: false, // Street name corrected
  AC11: false, // Street type changed (e.g., "St" to "Rd")
  AC12: false, // Street directional changed (e.g., "N" to "NW")
  AC13: false, // Suite type changed (e.g., "STE" to "APT")
  AC14: false, // Suite number changed
  AC15: true, // Double dependent locality changed
  AC16: true, // SubAdministrative area changed
  AC17: true, // SubNational area changed
  AC18: false, // PO Box changed
  AC19: true, // Premise type changed
  AC20: false, // House number changed
  AC22: false, // Organization changed
};

@Injectable({
  providedIn: 'root',
})
/**
 * Provides helper methods for address population, validation,
 * verification, and suggested address handling.
 */
export class AddressHelperService implements OnDestroy {
  private destroy$ = new Subject<void>();

  // Delay before moving focus to the verification outcome (suggested card / warning). Gives the
  // fin-message component + its shadow DOM time to render so the focus target exists. Kept local
  // to the service (distinct from the component's own auto-save delay, which serves a different step).
  private static readonly FOCUS_DELAY_MS = 300;

  constructor(private http: HttpClient) {}

  /**
   * Builds the full form control path for accessing nested or flat form controls.
   * When pathPrefix is empty (modal flat context), returns just the controlName.
   * When pathPrefix is provided (nested form context), returns `${pathPrefix}.${controlName}`.
   *
   * @param pathPrefix - The dot-notated path prefix (e.g., 'personalInformation.address') or empty string for flat forms.
   * @param controlName - The form control name (e.g., 'street1', 'city').
   * @returns The resolved path string to use with formGroup.get().
   */
  buildFormPath(pathPrefix: string, controlName: string): string {
    return pathPrefix ? `${pathPrefix}.${controlName}` : controlName;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Auto populates the Typeahead Address suggestion when selected a option, to the respective individual fields like addressline1, city, state.
   */
  populateAddressFields(field, item, pathPrefix: string) {
    const address = typeof item?.Address === 'object' ? item.Address : item;
    const formGroup = field?.form;

    if (!address) {
      return;
    }

    const addressLine1 = address.Address1 || address.DeliveryAddress1 || '';
    const city = address.Locality || '';
    const state = address.AdministrativeArea || '';
    const postalCode = address.PostalCode || address.PostalCodePrimary || '';
    const country = address.ISO3166_2 === 'US' ? 'USA' : address.ISO3166_2 || 'USA';

    if (field.formControl) {
      // setTimeout(0) ensures setValue runs after the typeahead's internal labelExpression update
      setTimeout(() => {
        field.formControl.setValue(addressLine1);
        if (field.formControl.markAsDirty) {
          field.formControl.markAsDirty();
        }
      });
    }

    if (formGroup) {
      const street2Control = formGroup.get(this.buildFormPath(pathPrefix, 'street2'));
      const cityControl = formGroup.get(this.buildFormPath(pathPrefix, 'city'));
      const stateControl = formGroup.get(this.buildFormPath(pathPrefix, 'state'));
      const postalCodeControl = formGroup.get(this.buildFormPath(pathPrefix, 'postalCode'));
      const countryControl = formGroup.get(this.buildFormPath(pathPrefix, 'country'));
      const verificationStatusControl = formGroup.get(this.buildFormPath(pathPrefix, 'addressVerificationStatus'));
      const poBoxControl = formGroup.get(this.buildFormPath(pathPrefix, 'poBoxIndicator'));
      const addressTypeControl = formGroup.get(this.buildFormPath(pathPrefix, 'addressType'));

      if (street2Control) street2Control.patchValue('');
      if (cityControl) cityControl.patchValue(city);
      if (stateControl) stateControl.patchValue(state);
      if (postalCodeControl) postalCodeControl.patchValue(postalCode);
      if (countryControl) countryControl.patchValue(country);
      if (verificationStatusControl) verificationStatusControl.patchValue('unverified');
      if (poBoxControl) poBoxControl.patchValue(false);
      if (addressTypeControl) addressTypeControl.patchValue('U');
    }

    if (field.options?.detectChanges) {
      field.options.detectChanges(field);
    }
  }
  /**
   * Initializes address option fields and nests them with the specified path prefix.
   */
  initAddressOptions(
    pathPrefix,
    overrideOptions?: Partial<Pick<AddressOptions, 'allowPOBox' | 'lastUserVerifiedAddress'>>,
  ) {
    const options: AddressOptions = {
      suggestedAddress: null,
      addressUndeliverableMessage: null,
      allowPOBox: true,
      lastUserVerifiedAddress: null,
      ...overrideOptions,
    };
    if (pathPrefix.length == 0) {
      return options;
    }
    return pathPrefix.split('.').reduceRight((acc, key) => ({ [key]: acc }), options as object);
  }
  /**
   * Set verify Address which can be used for change detection onLoad
   * at form initialization so that verifyAddress can detect edits
   * without making a redundant API call on the first blur.
   */
  setVerifyAddress(formState: any, pathPrefix: string, addressModel: any): void {
    if (!addressModel) return;
    this.setNestedFieldOptionValue(formState, pathPrefix, 'lastUserVerifiedAddress', {
      street1: addressModel.street1 || '',
      street2: addressModel.street2 || '',
      city: addressModel.city || '',
      state: addressModel.state || '',
      postalCode: addressModel.postalCode || '',
      country: addressModel.country || 'USA',
    });
  }
  /**
   * Updates a property at the specified nested path, creating intermediate objects when absent.
   */
  setNestedFieldOptionValue(obj: any, path: string, key: keyof AddressOptions, value: any): void {
    const ref = path ? path.split('.').reduce((acc, segment) => (acc[segment] ??= {}), obj) : obj;
    ref[key] = value;
  }
  /**
   * Validates address fields and returns the constructed address object.
   */
  validateAddress(field, pathPrefix): { isValid: boolean; address: Record<string, string> | null } {
    const formGroup = field?.form || field?.options?._buildForm?.form;
    if (!formGroup) return { isValid: false, address: null };

    const street1Control = formGroup.get(this.buildFormPath(pathPrefix, 'street1'));
    const street2Control = formGroup.get(this.buildFormPath(pathPrefix, 'street2'));
    const cityControl = formGroup.get(this.buildFormPath(pathPrefix, 'city'));
    const stateControl = formGroup.get(this.buildFormPath(pathPrefix, 'state'));
    const postalCodeControl = formGroup.get(this.buildFormPath(pathPrefix, 'postalCode'));
    const countryControl = formGroup.get(this.buildFormPath(pathPrefix, 'country'));

    const address = {
      street1: street1Control?.value || '',
      street2: street2Control?.value || '',
      city: cityControl?.value || '',
      state: stateControl?.value || '',
      postalCode: postalCodeControl?.value || '',
      country: countryControl?.value || 'USA',
    };

    // Skip verification if any required field has errors (empty or invalid)
    if (
      street1Control?.errors ||
      cityControl?.errors ||
      stateControl?.errors ||
      postalCodeControl?.errors ||
      countryControl?.errors
    ) {
      return {
        isValid: false,
        address: null,
      };
    }

    return {
      isValid: true,
      address: address,
    };
  }
  // Builds the shared verify HTTP request (validate + unchanged-skip + Canada mapping); null if skipped.
  private buildVerifyRequest(
    field,
    pathPrefix: string,
  ): { request: Observable<VerifyAddressResponse>; address: Record<string, string>; formGroup: any } | null {
    const formGroup = field?.form || field?.options?._buildForm?.form;
    if (!formGroup) return null;

    const { address, isValid } = this.validateAddress(field, pathPrefix);
    if (!isValid) return null;

    // prevent a redundant verify call if the address matches what we last verified
    const options = this.getAddressOptions(field, pathPrefix);
    const isDifferent =
      options?.lastUserVerifiedAddress !== null ? this.compareAddress(address, options.lastUserVerifiedAddress) : true;
    if (!isDifferent) return null;

    const verifyUrl = `${environment.urls.addressLookup}/address-validation/verify`;
    let country = address.country;
    let state = address.state;
    if (Object.values(CANADA_PROVINCE_TO_NMLS).includes(country)) {
      country = 'CAN';
      state = address.country?.substring(0, 2);
    }
    const params = new HttpParams().appendAll({
      a1: address.street1,
      a2: address.street2,
      loc: address.city,
      admarea: state,
      postal: address.postalCode,
      ctry: country,
    });
    const request = this.http.get<VerifyAddressResponse>(verifyUrl, { params, withCredentials: true });
    return { request, address, formGroup };
  }

  // Blur/change verification entry point (fire-and-forget subscribe).
  verifyAddress(field, pathPrefix) {
    const verificationData = this.buildVerifyRequest(field, pathPrefix);
    if (!verificationData) return;

    verificationData.request.pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => this.handleVerifySuccess(data, field, pathPrefix, verificationData.address),
      error: () => this.handleVerifyError(field, pathPrefix, verificationData.address),
    });
  }

  // Save-triggered verification; returns a Promise so the modal can await and decide whether to auto-save/close.
  verifyAddressOnSave(field, pathPrefix: string): Promise<VerifySaveResult> {
    const verificationData = this.buildVerifyRequest(field, pathPrefix);
    if (!verificationData) return Promise.resolve({ action: 'proceed' });

    return firstValueFrom(
      verificationData.request.pipe(
        map((data) => {
          this.handleVerifySuccess(data, field, pathPrefix, verificationData.address);
          this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'lastUserVerifiedAddress', {
            street1: verificationData.address.street1,
            street2: verificationData.address.street2,
            city: verificationData.address.city,
            state: verificationData.address.state,
            postalCode: verificationData.address.postalCode,
            country: verificationData.address.country,
          });
          const status = verificationData.formGroup.get(
            this.buildFormPath(pathPrefix, 'addressVerificationStatus'),
          )?.value;
          if (status === AddressVerificationStatus.SUGGESTED) return { action: 'suggested' as const };
          if (status === AddressVerificationStatus.UNVERIFIED) return { action: 'undeliverable' as const };
          if (status === AddressVerificationStatus.UNDELIVERABLE) return { action: 'undeliverable' as const };
          return { action: 'proceed' as const };
        }),
        catchError(() => {
          this.handleVerifyError(field, pathPrefix, verificationData.address);
          return of({ action: 'error' as const });
        }),
        takeUntil(this.destroy$),
        // If destroy$ tears the stream down mid-save, firstValueFrom would reject on an empty
        // completion; default to 'proceed' so a torn-down modal resolves cleanly instead.
        defaultIfEmpty({ action: 'proceed' as const }),
      ),
    );
  }

  private handleVerifySuccess(data: VerifyAddressResponse, field, pathPrefix, address) {
    const formGroup = field?.form || field?.options?._buildForm?.form;
    const record = data?.Records?.[0];
    const resultCodes = record?.Results || '';
    const validResultCodes = ['AV25', 'AV24'];
    const verificationStatusControl = formGroup.get(this.buildFormPath(pathPrefix, 'addressVerificationStatus'));

    if (validResultCodes.find((code) => resultCodes.indexOf(code) !== -1)) {
      let suggestedStreet1 = record?.AddressLine1 || '';
      const suggestedStreet2 = record?.SubPremises || '';
      const suggestedCity = record?.Locality || '';
      const suggestedState = record?.AdministrativeArea || '';
      const suggestedPostal = record?.PostalCode || '';

      const poBoxIndicator = !!record?.PostBox;
      const deliveryIndicator = record?.DeliveryIndicator || 'U';
      const addressType = deliveryIndicator === 'R' ? 'R' : deliveryIndicator === 'B' ? 'B' : 'U';
      const suggestedCountry = record?.CountryISO3166_1_Alpha3 || 'USA';

      if (suggestedStreet2.length > 0) {
        suggestedStreet1 = suggestedStreet1.replace(suggestedStreet2, '').trim();
      }
      const isDifferent = this.compareAddress(address, {
        street1: suggestedStreet1,
        street2: suggestedStreet2,
        city: suggestedCity,
        state: suggestedState,
        postalCode: suggestedPostal,
        country: suggestedCountry,
      });

      // When a PO Box is verified WITH corrections in a section that disallows PO
      // Boxes, present the standardized address as a suggestion instead of only showing the
      // "PO Box not allowed" warning. The warning applies only when the address is fully verified.
      const options = this.getAddressOptions(field, pathPrefix);
      if (options.allowPOBox === false && poBoxIndicator && !isDifferent) {
        const poBoxControl = formGroup.get(this.buildFormPath(pathPrefix, 'poBoxIndicator'));
        const addressTypeControl = formGroup.get(this.buildFormPath(pathPrefix, 'addressType'));
        if (verificationStatusControl) verificationStatusControl.patchValue(AddressVerificationStatus.VERIFIED);
        if (poBoxControl) poBoxControl.patchValue(true);
        if (addressTypeControl) addressTypeControl.patchValue(addressType);
        this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'suggestedAddress', null);
        this.setNestedFieldOptionValue(
          field.options.formState,
          pathPrefix,
          'addressUndeliverableMessage',
          WARNING_MESSAGES.NOT_ALLOW_POBOX,
        );
        this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'lastUserVerifiedAddress', {
          street1: address.street1,
          street2: address.street2,
          city: address.city,
          state: address.state,
          postalCode: address.postalCode,
          country: address.country,
        });
        this.triggerChangeDetection(field);
        this.focusAddressOutcomeMessage();
        return;
      }

      if (isDifferent && suggestedStreet1) {
        if (verificationStatusControl) verificationStatusControl.patchValue(AddressVerificationStatus.SUGGESTED);
        this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'suggestedAddress', {
          street1: suggestedStreet1,
          street2: suggestedStreet2,
          city: suggestedCity,
          state: suggestedState,
          postalCode: suggestedPostal,
          country: suggestedCountry,
          poBoxIndicator,
          addressType,
          resultCodes,
        });
        this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'addressUndeliverableMessage', null);
        this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'lastUserVerifiedAddress', {
          street1: address.street1,
          street2: address.street2,
          city: address.city,
          state: address.state,
          postalCode: address.postalCode,
          country: address.country,
        });
        this.focusAddressOutcomeMessage();
      } else {
        const poBoxControl = formGroup?.get(this.buildFormPath(pathPrefix, 'poBoxIndicator'));
        const addressTypeControl = formGroup?.get(this.buildFormPath(pathPrefix, 'addressType'));
        if (poBoxControl) poBoxControl.patchValue(poBoxIndicator);
        if (addressTypeControl) addressTypeControl.patchValue(addressType);
        if (verificationStatusControl) verificationStatusControl.patchValue(AddressVerificationStatus.VERIFIED);
        this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'suggestedAddress', null);
        this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'addressUndeliverableMessage', null);
        this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'lastUserVerifiedAddress', {
          street1: address.street1,
          street2: address.street2,
          city: address.city,
          state: address.state,
          postalCode: address.postalCode,
          country: address.country,
        });
      }
    } else if (resultCodes.includes('AV14')) {
      // AV14: Apartment/suite number not provided. This is "unverified" (deliverability could not
      // be confirmed for lack of a unit), which is distinct from a genuinely undeliverable address.
      if (verificationStatusControl) verificationStatusControl.patchValue(AddressVerificationStatus.UNVERIFIED);
      const poBoxControl = formGroup?.get(this.buildFormPath(pathPrefix, 'poBoxIndicator'));
      const addressTypeControl = formGroup?.get(this.buildFormPath(pathPrefix, 'addressType'));
      if (poBoxControl) poBoxControl.patchValue(false);
      if (addressTypeControl) addressTypeControl.patchValue('U');
      this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'suggestedAddress', null);
      this.setNestedFieldOptionValue(
        field.options.formState,
        pathPrefix,
        'addressUndeliverableMessage',
        WARNING_MESSAGES.REQUIRED_ADDRESS_LINE2,
      );
      this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'lastUserVerifiedAddress', {
        street1: address.street1,
        street2: address.street2,
        city: address.city,
        state: address.state,
        postalCode: address.postalCode,
        country: address.country,
      });
      this.focusAddressOutcomeMessage();
    } else {
      const poBoxControl = formGroup?.get(this.buildFormPath(pathPrefix, 'poBoxIndicator'));
      const addressTypeControl = formGroup?.get(this.buildFormPath(pathPrefix, 'addressType'));
      if (verificationStatusControl) verificationStatusControl.patchValue(AddressVerificationStatus.UNDELIVERABLE);
      if (poBoxControl) poBoxControl.patchValue(false);
      if (addressTypeControl) addressTypeControl.patchValue('U');
      this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'suggestedAddress', null);
      this.setNestedFieldOptionValue(
        field.options.formState,
        pathPrefix,
        'addressUndeliverableMessage',
        WARNING_MESSAGES.NOT_VERIFIED_ADDRESS,
      );
      this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'lastUserVerifiedAddress', {
        street1: address.street1,
        street2: address.street2,
        city: address.city,
        state: address.state,
        postalCode: address.postalCode,
        country: address.country,
      });
      this.focusAddressOutcomeMessage();
    }

    this.triggerChangeDetection(field);
  }

  private compareAddress(userTypedAddress, suggestedAddress) {
    const isUSA = userTypedAddress.country === 'USA';
    const userStreet2 = userTypedAddress.street2 || '';
    const suggestedStreet2 = suggestedAddress.street2 || '';
    const userTypedAddressLine = (
      userStreet2.length == 0 ? userTypedAddress.street1 : `${userTypedAddress.street1} ${userStreet2}`
    ).trim();
    const suggestedAddressLine = (
      suggestedStreet2.length == 0 ? suggestedAddress.street1 : `${suggestedAddress.street1} ${suggestedStreet2}`
    ).trim();
    return (
      suggestedAddressLine.toLowerCase() !== userTypedAddressLine?.toLowerCase() ||
      (suggestedAddress.city || '').toLowerCase() !== (userTypedAddress.city || '').toLowerCase() ||
      (isUSA && (suggestedAddress.state || '').toLowerCase() !== (userTypedAddress.state || '').toLowerCase()) ||
      (suggestedAddress.postalCode || '').toLowerCase().replace(/-/g, '') !==
        (userTypedAddress.postalCode || '').toLowerCase().replace(/-/g, '')
    );
  }

  private handleVerifyError(field, pathPrefix, address?) {
    const formGroup = field?.form || field?.options?._buildForm?.form;
    const verificationStatusControl = formGroup.get(this.buildFormPath(pathPrefix, 'addressVerificationStatus'));
    const poBoxControl = formGroup?.get(this.buildFormPath(pathPrefix, 'poBoxIndicator'));
    const addressTypeControl = formGroup?.get(this.buildFormPath(pathPrefix, 'addressType'));
    if (verificationStatusControl) verificationStatusControl.patchValue(AddressVerificationStatus.SERVICEUNAVAILABLE);
    if (poBoxControl) poBoxControl.patchValue(false);
    if (addressTypeControl) addressTypeControl.patchValue('U');
    this.setNestedFieldOptionValue(
      field.options.formState,
      pathPrefix,
      'addressUndeliverableMessage',
      WARNING_MESSAGES.SERVER_ERROR,
    );
    if (address) {
      this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'lastUserVerifiedAddress', {
        street1: address.street1,
        street2: address.street2,
        city: address.city,
        state: address.state,
        postalCode: address.postalCode,
        country: address.country,
      });
    }
    this.triggerChangeDetection(field);
    this.focusAddressOutcomeMessage();
  }

  private triggerChangeDetection(field) {
    if (field.options?.detectChanges) {
      field.options.detectChanges(field);
    }
    if (field.formControl) {
      field.formControl.updateValueAndValidity();
    }
    if (field.form) {
      field.form.updateValueAndValidity({ emitEvent: true });
    }
  }

  // 508: move focus to the suggested-address card or warning when it appears so keyboard/SR users are taken to it.
  private focusAddressOutcomeMessage(): void {
    // Wait for the verification result to render (fin-message + shadow DOM) before focusing.
    setTimeout(() => {
      // Scope to the open modal overlay (panelClass 'modal') so we target this form's message only.
      const overlay = document.querySelector('.cdk-overlay-pane.modal') as HTMLElement | null;
      if (!overlay) return;

      const suggestion = Array.from(overlay.querySelectorAll('.suggest-address-container')).find(
        (el: any) => el.offsetParent,
      ) as HTMLElement | undefined;
      if (suggestion) {
        const adrEle = suggestion.querySelector('fin-message') as HTMLElement;
        adrEle?.shadowRoot?.querySelector('div')?.focus();
        return;
      }

      const warning = Array.from(overlay.querySelectorAll('fin-message')).find(
        (el: any) => el.offsetParent && !el.closest('.suggest-address-container'),
      ) as HTMLElement | undefined;
      if (warning) {
        const guideLineEle = warning.shadowRoot?.querySelector('finra-guidelines') as HTMLElement;
        guideLineEle?.focus();
      }
    }, AddressHelperService.FOCUS_DELAY_MS);
  }

  /**
   * Retrieves address verification options from formState.
   * Resolves the AddressOptions object stored at the specified-nested path.
   */
  getAddressOptions(field, pathPrefix): AddressOptions | null {
    if (!pathPrefix) return field?.options?.formState;
    return pathPrefix.split('.').reduce((acc, key) => acc?.[key], field?.options?.formState);
  }

  // Applies the suggested address + metadata and marks verified; returns true if applied, false if nothing to accept.
  acceptSuggestedAddress(field, pathPrefix: string): boolean {
    const formGroup = field?.form || field?.options?._buildForm?.form;
    const options = this.getAddressOptions(field, pathPrefix);
    const suggested = options?.suggestedAddress;
    if (!formGroup || !suggested) {
      return false;
    }

    const street1Control = formGroup.get(this.buildFormPath(pathPrefix, 'street1'));
    const street2Control = formGroup.get(this.buildFormPath(pathPrefix, 'street2'));
    const cityControl = formGroup.get(this.buildFormPath(pathPrefix, 'city'));
    const stateControl = formGroup.get(this.buildFormPath(pathPrefix, 'state'));
    const postalCodeControl = formGroup.get(this.buildFormPath(pathPrefix, 'postalCode'));
    const countryControl = formGroup.get(this.buildFormPath(pathPrefix, 'country'));
    let suggestedCountry = suggested.country;
    if (suggested.country === 'CAN') {
      suggestedCountry = suggested.state + 'C';
    }
    if (street1Control) street1Control.patchValue(suggested.street1);
    if (street2Control) street2Control.patchValue(suggested.street2);
    if (cityControl) cityControl.patchValue(suggested.city);
    if (stateControl) stateControl.patchValue(suggested.country === 'USA' ? suggested.state : null);
    if (postalCodeControl) postalCodeControl.patchValue(suggested.postalCode);
    if (countryControl) countryControl.patchValue(suggestedCountry);

    const poBoxControl = formGroup.get(this.buildFormPath(pathPrefix, 'poBoxIndicator'));
    const addressTypeControl = formGroup.get(this.buildFormPath(pathPrefix, 'addressType'));
    const verificationStatusControl = formGroup.get(this.buildFormPath(pathPrefix, 'addressVerificationStatus'));
    if (poBoxControl) poBoxControl.patchValue(suggested.poBoxIndicator ?? false);
    this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'lastUserVerifiedAddress', {
      street1: suggested.street1,
      street2: suggested.street2,
      city: suggested.city,
      state: suggested.state,
      postalCode: suggested.postalCode,
      country: suggested.country,
    });
    if (addressTypeControl) addressTypeControl.patchValue(suggested.addressType ?? 'U');
    if (verificationStatusControl) verificationStatusControl.patchValue(AddressVerificationStatus.VERIFIED);
    this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'suggestedAddress', null);
    this.triggerChangeDetection(field);
    return true;
  }
  // Reject: substantive AC codes -> undeliverable (returns true, keep open); cosmetic-only -> verified (returns false).
  rejectSuggestedAddress(field, pathPrefix: string): boolean {
    const formGroup = field?.form || field?.options?._buildForm?.form;
    const options = this.getAddressOptions(field, pathPrefix);
    const suggested = options?.suggestedAddress;
    if (!formGroup || !suggested) {
      return false;
    }

    const resultCodes: string = suggested.resultCodes || '';
    const hasSubstantiveChange = resultCodes
      .split(',')
      .some((code) => AC_CODE_VERIFIED_MAP[code.trim().toUpperCase()] === false);

    const verificationStatusControl = formGroup.get(this.buildFormPath(pathPrefix, 'addressVerificationStatus'));
    const poBoxControl = formGroup.get(this.buildFormPath(pathPrefix, 'poBoxIndicator'));
    const addressTypeControl = formGroup.get(this.buildFormPath(pathPrefix, 'addressType'));

    if (hasSubstantiveChange) {
      if (verificationStatusControl) verificationStatusControl.patchValue(AddressVerificationStatus.UNDELIVERABLE);
      if (poBoxControl) poBoxControl.patchValue(false);
      if (addressTypeControl) addressTypeControl.patchValue('U');
      this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'suggestedAddress', null);
      this.setNestedFieldOptionValue(
        field.options.formState,
        pathPrefix,
        'addressUndeliverableMessage',
        WARNING_MESSAGES.NOT_VERIFIED_ADDRESS,
      );
      this.focusAddressOutcomeMessage();
    } else {
      if (poBoxControl) poBoxControl.patchValue(suggested.poBoxIndicator ?? false);
      if (addressTypeControl) addressTypeControl.patchValue(suggested.addressType ?? 'U');
      if (verificationStatusControl) verificationStatusControl.patchValue(AddressVerificationStatus.VERIFIED);
      this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'suggestedAddress', null);
      this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'addressUndeliverableMessage', null);
    }

    this.triggerChangeDetection(field);
    return hasSubstantiveChange;
  }

  // Clears suggestion + warning and resets status to UNVERIFIED so the next Save re-verifies the edited address.
  clearOptions(field: any, pathPrefix: string) {
    this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'suggestedAddress', null);
    this.setNestedFieldOptionValue(field.options.formState, pathPrefix, 'addressUndeliverableMessage', null);
    const formGroup = field?.form || field?.options?._buildForm?.form;
    const verificationStatusControl = formGroup?.get(this.buildFormPath(pathPrefix, 'addressVerificationStatus'));
    if (verificationStatusControl) verificationStatusControl.patchValue(AddressVerificationStatus.UNVERIFIED);
  }
}
